<template>
  <div class="work-instructions-container">
    <WorkInstructionsHeader />

    <div class="wrapper">
      <div class="items-container" v-if="currentPackage">
        <div
          v-for="(instruction, wiIndex) in workInstructions"
          :key="instruction._id"
        >
          <b-collapse animation="slide" :aria-id="instruction._id">
            <template #trigger="props">
              <div class="card work-instruction-card">
                <div class="card-header">
                  <div class="instruction-title">
                    <div>
                      {{ wiIndex + 1 }}. <span>{{ instruction.title }}</span>
                    </div>
                    <b-field>
                      <b-tag
                        :type="
                          getCompletedSteps(instruction) !==
                          instruction.steps.length
                            ? 'is-danger'
                            : 'is-success'
                        "
                        size="is-medium"
                        >{{ getCompletedSteps(instruction) }} /
                        {{ instruction.steps.length }}</b-tag
                      >
                    </b-field>
                  </div>
                </div>

                <DisplayRichTextEditor :value="instruction.description" />

                <div class="btn-expand">
                  <b-icon :icon="props.open ? 'menu-down' : 'menu-up'">
                  </b-icon>
                </div>
              </div>
            </template>

            <div class="work-instruction-container">
              <template v-if="instruction.steps && instruction.steps.length">
                <div
                  class="instruction wi-step card work-instruction-step-card"
                  v-for="(step, stepIndex) in instruction.steps"
                  :key="step._id || stepIndex"
                >
                  <div class="instruction-title card-header">
                    <div class="step-header-content">
                      <span class="step-index"
                        >{{ wiIndex + 1 }}.{{ stepIndex + 1 }}</span
                      >
                      <DisplayRichTextEditor :value="step.description" />
                    </div>
                    <b-button
                      type="is-success"
                      :disabled="!disableSignOffButton(step)"
                      @click="onSignOff(instruction, step)"
                      v-if="!step.signedBy"
                      >{{ $t('sign_off') }}</b-button
                    >
                    <div v-else class="sign-off">
                      <div class="sign-off-by">
                        Signed Off by {{ step.signedByFullName }}
                      </div>
                      <div class="sign-off-at">
                        {{ new Date(step.signedAt).toLocaleString() }}
                      </div>
                    </div>
                  </div>

                  <div class="step-attachemnts-container">
                    <div class="columns">
                      <div
                        class="column is-half"
                        v-if="step.linkedTasks && step.linkedTasks.length > 0"
                      >
                        <div class="attachment-label">
                          <img
                            src="/img/diagram_icon.png"
                            style="width: 24px; height: 24px"
                          />
                          Linked Tasks
                        </div>
                        <TasksDisplayGrid
                          :tasks="step.linkedTasks || []"
                          @task-click="onTaskClick"
                        />
                      </div>
                      <div
                        class="column is-half"
                        v-if="
                          step.linkedDocuments &&
                          step.linkedDocuments.length > 0
                        "
                      >
                        <div class="attachment-label">
                          <b-icon icon="file-document-arrow-right"></b-icon>
                          Linked Files
                        </div>
                        <LinkedDocuments :documents="step.linkedDocuments" />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </b-collapse>
        </div>
      </div>

      <div v-else class="empty-state">
        Select a work instruction package to view its instructions.
      </div>
    </div>
  </div>
</template>

<script>
import { GET_JOB } from '../../store/jobs/actions/actionTypes';
import JobViewMixin from '../../mixins/Shared/JobViewMixin';
import WorkInstructionsHeader from '../Shared/WorkInstructions/Header';
import {
  FETCH_WORK_INSTRUCTION_PACKAGES,
  FETCH_WORK_INSTRUCTIONS_FOR_PACKAGE,
  SIGN_OFF_STEP,
} from '../../store/workInstruction/actions/actionTypes';
import {
  GET_WORK_INSTRUCTION_PACKAGES,
  CURRENT_WORK_INSTRUCTION_PACKAGE,
  GET_WORK_INSTRUCTION,
} from '../../store/workInstruction/getters/getterTypes';
import { LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE } from '../../store/workInstruction/mutations/mutationTypes';
import DisplayRichTextEditor from '../RichTextEditor/DisplayRichTextEditor';
import LinkedDocuments from '../Shared/HyperlinkDocuments/LinkedDocuments';
import TasksDisplayGrid from '../Shared/TaskSelectModal/TasksDisplayGrid';

export default {
  mixins: [JobViewMixin],
  components: {
    WorkInstructionsHeader,
    DisplayRichTextEditor,
    LinkedDocuments,
    TasksDisplayGrid,
  },
  name: 'InspectorWorkInstructions',

  computed: {
    workInstructionPackages() {
      return this.$store.getters[GET_WORK_INSTRUCTION_PACKAGES];
    },
    currentPackage() {
      return this.$store.getters[CURRENT_WORK_INSTRUCTION_PACKAGE];
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    workInstructions() {
      return this.$store.getters[GET_WORK_INSTRUCTION];
    },
  },

  watch: {
    currentPackage(newPackage) {
      if (newPackage) {
        this.$store.dispatch(
          FETCH_WORK_INSTRUCTIONS_FOR_PACKAGE,
          newPackage._id
        );
        this.syncPackageQueryParam(newPackage._id);
      } else {
        this.syncPackageQueryParam(null);
      }
    },
  },

  async mounted() {
    const { id } = this.$route.params;
    await this.$store.dispatch(GET_JOB, id);
    await this.$store.dispatch(FETCH_WORK_INSTRUCTION_PACKAGES, id);

    const packageId = this.normalizePackageId(this.$route.query.package);
    if (!packageId) {
      return;
    }

    const selectedPackage = this.workInstructionPackages.find(
      (wiPackage) => wiPackage._id === packageId
    );
    if (selectedPackage) {
      this.$store.commit(
        LOCAL_SET_CURRENT_WORK_INSTRUCTION_PACKAGE,
        selectedPackage
      );
    }
  },

  methods: {
    getCompletedSteps(instruction) {
      return (instruction.steps || []).filter((step) => step.signedBy).length;
    },

    onSignOff(wi, step) {
      const timestamp = new Date().toISOString();
      const userFullName = this.currentUser.fullname;
      const userId = this.currentUser._id;
      this.$store.dispatch(SIGN_OFF_STEP, {
        instruction: wi,
        step,
        timestamp,
        userFullName,
        userId,
      });
    },

    disableSignOffButton(step) {
      if (!step.linkedTasks || step.linkedTasks.length === 0) return true;
      let allTasksAreCompleted = true;
      step.linkedTasks.forEach((task) => {
        if (!task.workStatusCode || task.workStatusCode.toString() !== '2') {
          allTasksAreCompleted = false;
        }
      });
      return allTasksAreCompleted;
    },

    normalizePackageId(value) {
      if (!value) {
        return null;
      }

      const normalizedValue = Array.isArray(value) ? value[0] : value;
      return String(normalizedValue)
        .trim()
        .replace(/^['"]|['"]$/g, '');
    },

    syncPackageQueryParam(packageId) {
      const normalizedPackageId = this.normalizePackageId(packageId);
      const currentPackageParam = this.normalizePackageId(
        this.$route.query.package
      );

      if (normalizedPackageId === currentPackageParam) {
        return;
      }

      const query = {
        ...this.$route.query,
      };

      if (normalizedPackageId) {
        query.package = normalizedPackageId;
      } else {
        delete query.package;
      }

      this.$router.replace({ query }).catch(() => {});
    },

    onTaskClick(task) {
      this.$router.push({
        name: 'inspector-template',
        params: { id: this.currentJob._id, taskNumber: task._id },
        query: { from: 'wi', package: this.currentPackage._id },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sign-off {
  color: #23d160;
  font-size: 1rem;
}

.card {
  border-radius: 10px;
}

.work-instruction-card {
  cursor: pointer;
}

.work-instructions-container {
  height: calc(100vh - 50px);
  width: 100%;
}

.wrapper {
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  padding: 20px 10px;
  text-align: center;
  justify-content: center;
  width: 100%;
  background-color: #dddddd;
}

.items-container {
  flex: 1;
  overflow: auto;
  overflow-x: hidden;
  gap: 10px;
  display: flex;
  flex-direction: column;
  padding: 0 10px;
}

.instruction-title {
  display: flex;
  font-size: larger;
  font-weight: bold;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  border-radius: 10px;
  font-size: x-large;
  padding: 10px;

  span {
    white-space: pre-wrap;
    text-align: left;
  }

  &.card-header {
    font-weight: normal;
    align-items: flex-start;
  }
}

.step-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  font-size: 1rem;
}

.step-index {
  font-weight: bold;
  font-size: x-large;
}

.wi-step {
  margin-left: 30px;
}

.work-instruction-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background-color: #dddddd;
}

.step-attachemnts-container {
  margin: 10px;
  width: 100%;
}

.btn-expand {
  display: flex;
  justify-content: start;
  padding: 5px;
  cursor: pointer;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background-color: white;
}

.collapse {
  background-color: #dddddd;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.attachment-label {
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  font-weight: 700;
  margin-bottom: 10px;
}

.empty-state {
  margin: auto;
  font-size: 18px;
  color: #555;
}
</style>
